import * as React from "react";
import {
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronRight,
  Loader2,
  Pencil,
  Sparkles,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { diffWords } from "@/lib/textDiff";
import {
  buildPrefill,
  ORIGIN_LABEL,
  SUGGESTED_ATTESTATION_RATIONALE,
  type NuancePrefill,
  type PrefillOrigin,
} from "@/lib/golden-prefill";
import {
  lookupInvestorGolden,
  proposeGoldenFromAnswer,
  type ChatBinding,
  type ChatSource,
  type GoldenSplitOffer,
  type ProposeGoldenResponse,
} from "@/lib/api";

/**
 * NuanceComposer — in-pane nuance capture (#145).
 *
 * An analyst reads an LP answer, judges it correct but incomplete, and knows the
 * one sentence that would make it right. This is where that sentence goes: it
 * becomes a governed **Pending** investor golden in one pass, recorded as an
 * explicit `gp_attested` span rather than laundered into looking document-backed.
 *
 * THE FAST PATH IS THE POINT. Default expanded state is ONE textarea and ONE
 * primary button. Every other golden field (`as_of`, `investors`, `category`,
 * `vehicle_label`, `confidence`, `freshness`) is derived from data already in
 * the browser and tucked behind the "Details" disclosure, where each chip says
 * where its value came from so an inferred value is distinguishable from an
 * observed one. A failed derivation leaves the field blank and NEVER blocks
 * submit — a seven-field governance form is a form nobody uses.
 *
 * gp_team only, enforced here AND by the route table AND by the handler's
 * in-handler role re-check.
 */

// Inclusive of 768px: the tablet portrait width IS one of the two narrow
// viewports the composer must not crush the chat thread at (#145 AC15), so the
// drawer takes over AT 768, not below it.
const NARROW_QUERY = "(max-width: 768px)";

const normalizeForDiff = (s: string) => s.replace(/\s+/g, " ").trim();

function useIsNarrow(): boolean {
  const [narrow, setNarrow] = React.useState(false);
  React.useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia(NARROW_QUERY);
    const apply = () => setNarrow(mq.matches);
    apply();
    mq.addEventListener?.("change", apply);
    return () => mq.removeEventListener?.("change", apply);
  }, []);
  return narrow;
}

export interface NuanceComposerProps {
  messageId: string;
  fund: string;
  fundName?: string | null;
  /** The user turn this answer replied to — the golden's identity. */
  question: string;
  /** The served answer text. */
  answerBody: string;
  bindings?: ChatBinding[];
  sources?: ChatSource[];
  className?: string;
  /** Test seam: skip the best-effort nearest-neighbour category lookup. */
  skipCategoryLookup?: boolean;
}

type Rejection = { code?: string; error: string; cause?: string; fix?: string };

export function NuanceComposer({
  messageId,
  fund,
  fundName,
  question,
  answerBody,
  bindings,
  sources,
  className,
  skipCategoryLookup,
}: NuanceComposerProps) {
  const isNarrow = useIsNarrow();
  const [open, setOpen] = React.useState(false);
  const [detailsOpen, setDetailsOpen] = React.useState(false);
  const [bodyOpen, setBodyOpen] = React.useState(false);

  const prefill = React.useMemo<NuancePrefill>(
    () =>
      buildPrefill({
        fund,
        fundName,
        question,
        answerBody,
        bindings,
        sources,
      }),
    [fund, fundName, question, answerBody, bindings, sources],
  );

  const [nuance, setNuance] = React.useState("");
  const [rationale, setRationale] = React.useState(SUGGESTED_ATTESTATION_RATIONALE);
  const [rationaleAcknowledged, setRationaleAcknowledged] = React.useState(false);
  const [questionDraft, setQuestionDraft] = React.useState(prefill.question);
  const [bodyDraft, setBodyDraft] = React.useState(prefill.answer_body);
  const [category, setCategory] = React.useState(prefill.category.value);
  const [categoryOrigin, setCategoryOrigin] = React.useState(prefill.category.origin);
  const [asOf, setAsOf] = React.useState(prefill.as_of.value);
  // Semicolon-separated, NOT comma — legal entity names contain commas
  // ("SCP Outpost Investor I, LLC"), so a comma split shreds them into halves.
  const [investors, setInvestors] = React.useState(prefill.investors.value.join("; "));
  const [confidence, setConfidence] = React.useState(prefill.confidence.value);
  const [freshness, setFreshness] = React.useState(prefill.freshness.value);
  const [vehicleLabel, setVehicleLabel] = React.useState(prefill.vehicle_label.value);

  const [submitting, setSubmitting] = React.useState(false);
  const [rejection, setRejection] = React.useState<Rejection | null>(null);
  const [split, setSplit] = React.useState<GoldenSplitOffer | null>(null);
  const [result, setResult] = React.useState<ProposeGoldenResponse | null>(null);

  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  const bodyEdited = bodyDraft.trim() !== prefill.answer_body.trim();

  // Best-effort nearest-neighbour category prefill. Every failure — embedding
  // error, RPC error, clean miss — is swallowed and the composer opens with a
  // blank category. A prefill hint must never be able to prevent a capture.
  React.useEffect(() => {
    if (!open || skipCategoryLookup || category) return;
    let alive = true;
    (async () => {
      try {
        const r = await lookupInvestorGolden(questionDraft, fund);
        const near = r.match?.category || r.candidates?.[0]?.category;
        if (alive && near) {
          setCategory(near);
          setCategoryOrigin("nearest-golden");
        }
      } catch {
        /* non-blocking by design */
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, skipCategoryLookup]);

  React.useEffect(() => {
    if (open) textareaRef.current?.focus();
  }, [open]);

  const composed = nuance.trim() ? `${bodyDraft.trim()}\n\n${nuance.trim()}` : bodyDraft.trim();
  const asOfMissing = prefill.as_of_required && !asOf.trim();
  const rationaleOk = !nuance.trim() || (rationale.trim().length > 0 && rationaleAcknowledged);
  const canSubmit =
    !submitting && (nuance.trim().length > 0 || bodyEdited) && rationaleOk && !asOfMissing;

  const reset = () => {
    setOpen(false);
    setDetailsOpen(false);
    setBodyOpen(false);
    setSplit(null);
    setRejection(null);
  };

  const submit = async (acceptSplit = false, overrideBody?: string) => {
    if (submitting) return;
    setSubmitting(true);
    setRejection(null);
    try {
      const body = (overrideBody ?? bodyDraft).trim();
      const res = await proposeGoldenFromAnswer({
        message_id: messageId,
        fund,
        question: questionDraft.trim(),
        answer_body: body,
        body_edited: body !== prefill.answer_body.trim(),
        nuance: nuance.trim() || undefined,
        attestation_rationale: nuance.trim() ? rationale.trim() : undefined,
        category: category.trim() || undefined,
        as_of: asOf.trim() || undefined,
        investors: investors
          .split(";")
          .map((s) => s.trim())
          .filter(Boolean),
        confidence: confidence.trim() || undefined,
        freshness: freshness.trim() || undefined,
        vehicle_label: vehicleLabel.trim() || undefined,
        bindings: prefill.bindings,
        sources: prefill.sources,
        ...(acceptSplit ? { accept_split: true } : {}),
      });
      setResult(res);
      setSplit(null);
    } catch (err) {
      const payload = (err as Error & { payload?: unknown }).payload as
        | (GoldenSplitOffer & Rejection)
        | undefined;
      if (payload?.code === "gated_source_split_available") {
        setSplit(payload as GoldenSplitOffer);
      } else if (payload?.error) {
        setRejection(payload);
      } else {
        setRejection({ error: err instanceof Error ? err.message : "Could not record the nuance" });
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <div className={cn("flex items-center gap-1.5 text-xs text-muted-foreground", className)}>
        <Check className="size-3.5 text-primary" />
        Captured as a Pending golden — review it in Admin ▸ Investor Goldens.
      </div>
    );
  }

  if (!open) {
    return (
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className={cn("h-7 gap-1 px-1.5 text-[11px] font-medium text-muted-foreground", className)}
        onClick={() => setOpen(true)}
      >
        <Sparkles className="size-[13px]" aria-hidden />
        Add nuance
      </Button>
    );
  }

  const form = (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-[12.5px] font-medium text-foreground">Add nuance</div>
          <p className="mt-0.5 text-[11.5px] leading-4 text-muted-foreground">
            The sentence that makes this answer right. It is recorded as your
            attestation and reviewed before it is ever served.
          </p>
        </div>
        <button
          type="button"
          aria-label="Close nuance composer"
          onClick={reset}
          className="grid size-6 shrink-0 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <X className="size-3.5" />
        </button>
      </div>

      {/* ── the one field ────────────────────────────────────────────── */}
      <Textarea
        ref={textareaRef}
        value={nuance}
        onChange={(e) => setNuance(e.target.value)}
        placeholder="What is missing from this answer?"
        aria-label="Nuance"
        className="min-h-20 text-sm"
        onKeyDown={(e) => {
          // Enter submits, Shift+Enter newlines — matches the chat input.
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            if (canSubmit) void submit();
          }
        }}
      />

      {nuance.trim() ? (
        <div className="rounded-md border border-border bg-muted/30 p-2.5">
          <label
            htmlFor={`rationale-${messageId}`}
            className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted-foreground"
          >
            Why this is GP knowledge
          </label>
          <Textarea
            id={`rationale-${messageId}`}
            value={rationale}
            onChange={(e) => {
              setRationale(e.target.value);
              setRationaleAcknowledged(true);
            }}
            className="mt-1.5 min-h-14 bg-card text-[12.5px]"
            aria-label="Attestation rationale"
          />
          {!rationaleAcknowledged ? (
            <label className="mt-2 flex items-start gap-2 text-[11.5px] text-muted-foreground">
              <input
                type="checkbox"
                className="mt-0.5 size-3.5 accent-[var(--primary)]"
                checked={rationaleAcknowledged}
                onChange={(e) => setRationaleAcknowledged(e.target.checked)}
              />
              <span>
                This suggested reason is accurate. (Edit it or confirm it — a
                default reason should not become a default excuse.)
              </span>
            </label>
          ) : null}
        </div>
      ) : null}

      {asOfMissing ? (
        <div className="flex items-start gap-1.5 rounded-md border border-warning/30 bg-warning-soft/40 p-2 text-[11.5px] text-warning">
          <AlertTriangle className="mt-px size-3.5 shrink-0" />
          <span>
            This answer states a figure, so it needs an as-of date before it can be
            banked. Add it under Details.
          </span>
        </div>
      ) : null}

      {/* ── primary action ───────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" size="sm" disabled={!canSubmit} onClick={() => void submit()}>
          {submitting ? <Loader2 className="size-3.5 animate-spin" /> : null}
          Submit
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={reset} disabled={submitting}>
          Cancel
        </Button>
      </div>

      {/* ── everything else, one click away ──────────────────────────── */}
      <div className="border-t pt-2">
        <button
          type="button"
          aria-expanded={detailsOpen}
          onClick={() => setDetailsOpen((v) => !v)}
          className="inline-flex items-center gap-1 text-[11.5px] font-medium text-muted-foreground transition-colors hover:text-foreground"
        >
          {detailsOpen ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
          Details
        </button>
        {detailsOpen ? (
          <div className="mt-2 grid gap-2 sm:grid-cols-2">
            <Chip label="Question" origin={prefill.question ? "chat" : "none"} className="sm:col-span-2">
              <input
                value={questionDraft}
                onChange={(e) => setQuestionDraft(e.target.value)}
                aria-label="Question"
                className="w-full bg-transparent text-[12.5px] text-foreground outline-none"
              />
            </Chip>
            <Chip label="Category" origin={categoryOrigin}>
              <input
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                aria-label="Category"
                className="w-full bg-transparent text-[12.5px] text-foreground outline-none"
              />
            </Chip>
            <Chip label="As of" origin={prefill.as_of.origin}>
              <input
                value={asOf}
                onChange={(e) => setAsOf(e.target.value)}
                placeholder="YYYY-MM-DD"
                aria-label="As of"
                className="w-full bg-transparent text-[12.5px] text-foreground outline-none"
              />
            </Chip>
            <Chip label="Investors (; separated)" origin={prefill.investors.origin} className="sm:col-span-2">
              <input
                value={investors}
                onChange={(e) => setInvestors(e.target.value)}
                aria-label="Investors"
                className="w-full bg-transparent text-[12.5px] text-foreground outline-none"
              />
            </Chip>
            <Chip label="Confidence" origin={prefill.confidence.origin}>
              <input
                value={confidence}
                onChange={(e) => setConfidence(e.target.value)}
                aria-label="Confidence"
                className="w-full bg-transparent text-[12.5px] text-foreground outline-none"
              />
            </Chip>
            <Chip label="Freshness" origin={prefill.freshness.origin}>
              <input
                value={freshness}
                onChange={(e) => setFreshness(e.target.value)}
                aria-label="Freshness"
                className="w-full bg-transparent text-[12.5px] text-foreground outline-none"
              />
            </Chip>
            <Chip label="Vehicle" origin={prefill.vehicle_label.origin} className="sm:col-span-2">
              <input
                value={vehicleLabel}
                onChange={(e) => setVehicleLabel(e.target.value)}
                aria-label="Vehicle"
                className="w-full bg-transparent text-[12.5px] text-foreground outline-none"
              />
            </Chip>
          </div>
        ) : null}
      </div>

      {/* ── edit the answer body (secondary) ─────────────────────────── */}
      <div>
        <button
          type="button"
          aria-expanded={bodyOpen}
          onClick={() => setBodyOpen((v) => !v)}
          className="inline-flex items-center gap-1 text-[11.5px] font-medium text-muted-foreground transition-colors hover:text-foreground"
        >
          <Pencil className="size-3" />
          Edit answer body
          {bodyEdited ? (
            <Badge variant="warning" className="ml-1 px-1 py-0 text-[10px]">
              edited
            </Badge>
          ) : null}
        </button>
        {bodyOpen ? (
          <Textarea
            value={bodyDraft}
            onChange={(e) => setBodyDraft(e.target.value)}
            aria-label="Answer body"
            className="mt-2 min-h-28 text-[12.5px]"
          />
        ) : null}
      </div>

      {/* ── diff preview: exactly what changes vs the served answer ──── */}
      {nuance.trim() || bodyEdited ? (
        <div className="rounded-md border border-border bg-muted/20 p-2.5">
          <div className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted-foreground">
            Diff preview
          </div>
          <p
            className="mt-1.5 whitespace-pre-wrap text-[12.5px] leading-5"
            data-testid="nuance-diff-preview"
          >
            {/* Whitespace-normalized on BOTH sides: the diff is about which
                WORDS changed, and without this the paragraph break introduced by
                appending the nuance re-tokenizes the last unchanged word into a
                spurious delete+insert pair. */}
            {diffWords(normalizeForDiff(prefill.answer_body), normalizeForDiff(composed)).map((t, i) =>
              t.type === "equal" ? (
                <span key={i} className="text-foreground/70">
                  {t.text}
                </span>
              ) : t.type === "insert" ? (
                <ins key={i} className="bg-primary/10 text-primary no-underline">
                  {t.text}
                </ins>
              ) : (
                <del key={i} className="text-muted-foreground/70">
                  {t.text}
                </del>
              ),
            )}
          </p>
        </div>
      ) : null}

      {/* ── the gated-source split offer ─────────────────────────────── */}
      {split ? (
        <div className="rounded-md border border-warning/30 bg-warning-soft/40 p-2.5" role="alert">
          <div className="flex items-start gap-1.5">
            <AlertTriangle className="mt-px size-3.5 shrink-0 text-warning" />
            <div className="min-w-0">
              <div className="text-[12px] font-medium text-foreground">
                A cited source is a gated document
              </div>
              <p className="mt-0.5 text-[11.5px] leading-4 text-muted-foreground">
                The claims can still be banked without the figures. The gated
                document is recorded in the audit trail only — it never reaches
                the golden.
              </p>
              <div className="mt-2 rounded border border-border bg-card p-2 text-[12px] whitespace-pre-wrap">
                {split.proposed_figure_free_answer || "(nothing would remain)"}
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                <Button
                  type="button"
                  size="sm"
                  disabled={submitting || !split.proposed_figure_free_answer.trim()}
                  onClick={() => {
                    setBodyDraft(split.proposed_figure_free_answer);
                    void submit(true, split.proposed_figure_free_answer);
                  }}
                >
                  {submitting ? <Loader2 className="size-3.5 animate-spin" /> : null}
                  Bank the claims without the figures
                </Button>
                <Button type="button" variant="ghost" size="sm" onClick={reset} disabled={submitting}>
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* ── governance rejection: problem → cause → fix ──────────────── */}
      {rejection ? (
        <div
          className="rounded-md border border-destructive/30 bg-destructive/5 p-2.5 text-[11.5px] text-destructive"
          role="alert"
        >
          <div className="font-medium">{rejection.error}</div>
          {rejection.cause ? <div className="mt-1 opacity-90">{rejection.cause}</div> : null}
          {rejection.fix ? <div className="mt-1 opacity-90">{rejection.fix}</div> : null}
        </div>
      ) : null}
    </div>
  );

  // At constrained widths the composer moves into a drawer so it never crushes
  // the chat thread it is commenting on.
  if (isNarrow) {
    return (
      <>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={cn("h-7 gap-1 px-1.5 text-[11px] font-medium text-muted-foreground", className)}
          onClick={() => setOpen(true)}
        >
          <Sparkles className="size-[13px]" aria-hidden />
          Add nuance
        </Button>
        <div
          className="fixed inset-0 z-[60] bg-[rgb(26_32_44_/_0.24)]"
          onClick={reset}
          aria-hidden="true"
        />
        <aside
          className="fixed inset-x-0 bottom-0 z-[61] max-h-[85vh] overflow-y-auto rounded-t-lg border-t bg-card p-4 shadow-[var(--shadow-lg)]"
          aria-label="Add nuance"
        >
          {form}
        </aside>
      </>
    );
  }

  return (
    <div className={cn("mt-2 w-full rounded-md border bg-card p-3", className)}>{form}</div>
  );
}

function Chip({
  label,
  origin,
  className,
  children,
}: {
  label: string;
  origin: PrefillOrigin;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn("rounded-md border border-border bg-muted/20 px-2 py-1.5", className)}
      title={ORIGIN_LABEL[origin]}
    >
      <div className="text-[10px] font-medium uppercase tracking-[0.12em] text-muted-foreground">
        {label}
      </div>
      <div className="mt-0.5">{children}</div>
    </div>
  );
}
